import { useEffect, useRef, useState, type CSSProperties, type Ref } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Music, Plus, Search, Volume2, VolumeX } from 'lucide-react'
import { useGuildStore } from '@/store/guilds'
import { useVoiceStore } from '@/store/voice'
import { ApiError } from '@/lib/api'
import { describeError } from '@/lib/auth'
import { guildIconUrl } from '@/lib/avatar'
import { cx } from '@/lib/cx'
import { playUrl } from '@/lib/sounds'
import { soundUrl, soundboardApi } from '@/lib/soundboard'
import type { Guild, SoundboardSound } from '@/types/api'
import { SoundEmoji } from './SoundEmoji'
import styles from './Soundboard.module.css'

interface Section {
  key: string
  title: string
  guild?: Guild
  sounds: SoundboardSound[]
}

interface Props {
  /** Почему играть нельзя (нет права, выключен звук); null — можно. Послушать самому можно всегда. */
  blocked: string | null
  /** Звуки других серверов: «Плюс» и USE_EXTERNAL_SOUNDS в канале. */
  external: boolean
  /** Добавить звук на текущий сервер; null — нет права или слоты заняты. */
  onAdd: (() => void) | null
  ref?: Ref<HTMLDivElement>
}

/**
 * Звуковая панель над доком: звуки текущего сервера, других серверов (с «Плюсом») и стандартные.
 * Нажатие на звук играет его всем в канале, значок динамика справа — послушать самому.
 */
export function Soundboard({ blocked, external, onAdd, ref }: Props) {
  const connection = useVoiceStore((s) => s.connection)
  const volume = useVoiceStore((s) => s.prefs.soundboardVolume)
  const output = useVoiceStore((s) => s.prefs.audiooutput)
  const setPrefs = useVoiceStore((s) => s.setPrefs)
  const sendSound = useVoiceStore((s) => s.sendSound)
  const guilds = useGuildStore((s) => s.guilds)
  const defaults = useQuery({ queryKey: ['soundboard-defaults'], queryFn: soundboardApi.defaults, staleTime: Infinity })
  const [query, setQuery] = useState('')
  const [volumeOpen, setVolumeOpen] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  // последний нажатый звук: плитка коротко вспыхивает, n перезапускает вспышку при повторе
  const [pulse, setPulse] = useState<{ id: string; n: number } | null>(null)
  const [active, setActive] = useState<string | null>(null)
  const search = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLDivElement>(null)
  const preview = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    search.current?.focus()
    return () => preview.current?.pause()
  }, [])

  const current = connection?.guildId ? guilds[connection.guildId] : undefined
  const sections: Section[] = []
  if (current && ((current.soundboard_sounds?.length ?? 0) > 0 || onAdd)) {
    sections.push({ key: current.id, title: current.name, guild: current, sounds: current.soundboard_sounds ?? [] })
  }
  if (external) {
    const others = Object.values(guilds)
      .filter((g) => g.id !== current?.id && (g.soundboard_sounds?.length ?? 0) > 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    for (const g of others) sections.push({ key: g.id, title: g.name, guild: g, sounds: g.soundboard_sounds ?? [] })
  }
  sections.push({ key: 'defaults', title: 'Стандартные', sounds: defaults.data ?? [] })

  const q = query.trim().toLocaleLowerCase('ru')
  const shown = q
    ? sections.map((s) => ({ ...s, sounds: s.sounds.filter((x) => x.name.toLocaleLowerCase('ru').includes(q) || x.emoji_name === query.trim()) })).filter((s) => s.sounds.length > 0)
    : sections

  const play = async (sound: SoundboardSound) => {
    setPulse((p) => ({ id: sound.sound_id, n: (p?.n ?? 0) + 1 }))
    setNotice(null)
    try {
      await sendSound(sound)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setNotice(`Не так часто: подождите ${Math.max(1, Math.ceil(err.retryAfter ?? 2))} с`)
      else setNotice(describeError(err).message)
    }
  }

  const listen = async (sound: SoundboardSound) => {
    preview.current?.pause()
    preview.current = await playUrl(soundUrl(sound.sound_id), { volume: sound.volume * volume, sinkId: output || undefined })
  }

  const jump = (key: string) => {
    setActive(key)
    setQuery('')
    requestAnimationFrame(() => list.current?.querySelector(`[data-section="${key}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
  }

  // значок раздела в рейке подсвечивается по тому, что сейчас наверху списка
  const onScroll = () => {
    const el = list.current
    if (!el) return
    let top: string | null = null
    for (const s of el.querySelectorAll<HTMLElement>('[data-section]')) {
      if (s.offsetTop - el.offsetTop <= el.scrollTop + 8) top = s.dataset.section ?? null
    }
    setActive(top)
  }

  const railActive = active ?? shown[0]?.key ?? null
  const muted = volume === 0
  return (
    <div ref={ref} className={cx(styles.board, 'klichat-popover')} role="dialog" aria-label="Звуковая панель" style={{ '--popover-origin': 'bottom left' } as CSSProperties}>
      <div className={styles.top}>
        <label className={styles.search}>
          <Search size={16} className={styles.searchIcon} aria-hidden="true" />
          <input ref={search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Найти звук" aria-label="Найти звук" />
        </label>
        <button type="button" className={styles.iconButton} aria-pressed={volumeOpen} data-tooltip="Громкость звуковой панели" aria-label="Громкость звуковой панели" onClick={() => setVolumeOpen((v) => !v)}>
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>
      {volumeOpen ? (
        <label className={styles.volume}>
          <span>Громкость панели</span>
          <input type="range" min={0} max={100} step={1} value={Math.round(volume * 100)} onChange={(e) => void setPrefs({ soundboardVolume: Number(e.target.value) / 100 })} />
          <span className={styles.volumeValue}>{Math.round(volume * 100)} %</span>
        </label>
      ) : null}
      <div className={styles.body}>
        <nav className={styles.rail} aria-label="Разделы">
          {sections.map((s) => (
            <button key={s.key} type="button" className={styles.railButton} data-active={railActive === s.key ? '' : undefined} data-tooltip={s.title} aria-label={s.title} onClick={() => jump(s.key)}>
              {s.guild ? <GuildMark guild={s.guild} /> : <Music size={18} />}
            </button>
          ))}
        </nav>
        <div ref={list} className={styles.list} onScroll={onScroll}>
          {blocked ? <p className={styles.notice}>{blocked}</p> : null}
          {notice ? <p className={cx(styles.notice, styles.noticeError)} role="alert">{notice}</p> : null}
          {shown.map((s) => (
            <section key={s.key} data-section={s.key} className={styles.section} aria-label={s.title}>
              <h3 className={styles.sectionTitle}>{s.title}</h3>
              <div className={styles.grid}>
                {s.key === connection?.guildId && onAdd && !q ? (
                  <button type="button" className={cx(styles.tile, styles.add)} onClick={onAdd}>
                    <Plus size={16} aria-hidden="true" />
                    <span className={styles.name}>Добавить звук</span>
                  </button>
                ) : null}
                {s.sounds.map((sound) => (
                  <div key={`${sound.sound_id}-${pulse?.id === sound.sound_id ? pulse.n : 0}`} className={styles.tile} data-played={pulse?.id === sound.sound_id ? '' : undefined}>
                    <button type="button" className={styles.play} disabled={Boolean(blocked)} data-tooltip={blocked ?? sound.name} onClick={() => void play(sound)}>
                      <SoundEmoji id={sound.emoji_id} name={sound.emoji_name} size={18} className={styles.emoji} />
                      <span className={styles.name}>{sound.name}</span>
                    </button>
                    <button type="button" className={styles.listen} data-tooltip="Послушать самому" aria-label={`Послушать «${sound.name}»`} onClick={() => void listen(sound)}>
                      <Volume2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}
          {q && shown.length === 0 ? <p className={styles.empty}>Звуков по запросу «{query.trim()}» нет</p> : null}
          {defaults.isError ? <p className={cx(styles.notice, styles.noticeError)}>Стандартные звуки не загрузились. Откройте панель ещё раз</p> : null}
        </div>
      </div>
    </div>
  )
}

/** Иконка сервера в рейке, без иконки — первые буквы названия. */
function GuildMark({ guild }: { guild: Guild }) {
  const icon = guildIconUrl(guild.id, guild.icon)
  if (icon) return <img src={icon} alt="" className={styles.guildIcon} />
  const initials = guild.name.split(/\s+/).map((w) => w[0] ?? '').join('').slice(0, 2)
  return <span className={styles.guildInitials}>{initials}</span>
}
